import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@/hooks/auth/use-user";
import { useAIOperationWrapper } from "@/hooks/ai-usage/useAIOperationWrapper";
import { useTaskGeneration } from "./ai-task-generation";
import { createSuggestedTask } from "./database-suggested-tasks";
import { useAIUsageData } from "@/hooks/subscription/useAIUsageData";
import { useGlobalModals } from "@/contexts/GlobalModalsContext";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getUnsentTaskContextForGoal, markTasksAsContextSent, buildTaskContextMessage } from "./task-context-service";

interface BulkTaskGenerationParams {
  goalId: string;
  goalTitle: string;
  goalDescription?: string;
  openaiThreadId?: string;
  userContext?: string; // NEW: User-provided context for task generation
}

/**
 * Generate 5 tasks in a single API call and parse the response
 */
async function generateBulkTasks(
  threadId: string,
  goalTitle: string,
  goalDescription: string,
  goalId: string,
  userId: string,
  executeAIOperation: (operation: () => Promise<any>, options?: any) => Promise<any>,
  userContext?: string // NEW: User-provided context
): Promise<Array<{ title: string; description: string }>> {
  if (!threadId) {
    throw new Error("Thread ID is required for bulk task generation");
  }

  // Fetch and include user task context if available
  let enhancedGoalDescription = goalDescription;
  
  console.log('[BulkTaskGeneration] Checking for unsent task context...');
  try {
    const userTaskContext = await getUnsentTaskContextForGoal(goalId, userId);
    if (userTaskContext) {
      const contextMessage = buildTaskContextMessage(userTaskContext);
      enhancedGoalDescription = goalDescription + contextMessage;
      console.log('[BulkTaskGeneration] Added user task context to prompt');
    } else {
      console.log('[BulkTaskGeneration] No unsent task context found');
    }
  } catch (error) {
    console.error('[BulkTaskGeneration] Error fetching task context:', error);
    // Continue without context rather than failing
  }

  // Build bulk task generation prompt
  const prompt = `You are a project management consultant who recommends actionable tasks to help users achieve their goals.

Goal: "${goalTitle}"
${enhancedGoalDescription ? `Description: "${enhancedGoalDescription}"` : ''}
${userContext ? `User Request: "${userContext}"` : ''}

Generate 5 specific, high-impact tasks that would significantly help the user make progress toward this goal. ${userContext ? 'Pay special attention to the user\'s specific request above and ensure the tasks align with their focus area.' : ''} Each task should be distinct and complementary, covering different aspects of goal achievement.

Your response should be EXACTLY in this format:
TASK 1:
TITLE: [2-6 word action-oriented title]
DESCRIPTION: [Detailed brief that specifies exactly what needs to be accomplished]

TASK 2:
TITLE: [2-6 word action-oriented title]
DESCRIPTION: [Detailed brief that specifies exactly what needs to be accomplished]

TASK 3:
TITLE: [2-6 word action-oriented title]
DESCRIPTION: [Detailed brief that specifies exactly what needs to be accomplished]

TASK 4:
TITLE: [2-6 word action-oriented title]
DESCRIPTION: [Detailed brief that specifies exactly what needs to be accomplished]

TASK 5:
TITLE: [2-6 word action-oriented title]
DESCRIPTION: [Detailed brief that specifies exactly what needs to be accomplished]

Requirements:
- Each title MUST include an action verb from this list: Create, Write, Research, Design, Illustrate, Capture, Send, Plan, Draft, Draw, Develop, Build, Analyze, Investigate, Explore, Examine, Review, Discover, Identify, Generate, Produce, Compose
- Each title MUST include a specific output noun from this list: Document, Analysis, Report, Checklist, Review, Art, Image, Picture, Graphic, Illustration, Photo, Article, Study, Investigation, Survey, Assessment, Strategy, Framework, Blueprint, Template, Guide, Manual, Proposal, Brief, Summary, Presentation, Video, Animation
- Each description should be specific and actionable - something the user can execute immediately
- Tasks should be clearly connected to their recent feedback.
- Focus on transformative, strategic tasks that create step-changes in opportunity rather than incremental improvements
- Be mindful of previous context, listen to user feedback, avoid repetition. Use the feedback for direction, then dig deeper and expand wider.
- CRITICAL: Every task title must use at least one action verb and one output noun from the specified lists above to ensure content generation opportunities are detected.`;

  const data = await executeAIOperation(
    async () => {
      const { data, error } = await supabase.functions.invoke("threaded-text-generation", {
        body: { 
          type: "ai-text-generation",
          thread_id: threadId,
          mode: 'generate',
          prompt: prompt,
          appName: "Task Manager",
          functionName: "Bulk Task Generation",
          context: {
            pageTitle: `Goal: ${goalTitle}`
          }
        }
      });
      
      if (error) {
        throw new Error(`Edge Function returned a non-2xx status code`);
      }

      if (!data?.content) {
        throw new Error("Error generating bulk tasks");
      }

      return data;
    },
    {
      immediate: true,
      errorPrefix: 'Bulk task generation failed',
      debug: false
    }
  );

  const content = data.content;
  
  // Mark tasks as context sent if we had context
  try {
    await markTasksAsContextSent(goalId, userId);
    console.log('[BulkTaskGeneration] Marked tasks as context sent');
  } catch (error) {
    console.error('[BulkTaskGeneration] Failed to mark tasks as context sent:', error);
    // Non-critical error, continue
  }

  // Parse the response to extract all 5 tasks
  const tasks: Array<{ title: string; description: string }> = [];
  const lines = content.split('\n').filter(line => line.trim());
  
  let currentTask: { title?: string; description?: string } = {};
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    
    // Check if this is a task number marker
    if (trimmedLine.match(/^TASK \d+:$/)) {
      // Save previous task if complete
      if (currentTask.title && currentTask.description) {
        tasks.push({
          title: currentTask.title,
          description: currentTask.description
        });
      }
      // Reset for new task
      currentTask = {};
    } else if (trimmedLine.startsWith('TITLE:')) {
      currentTask.title = trimmedLine.substring(6).trim();
    } else if (trimmedLine.startsWith('DESCRIPTION:')) {
      currentTask.description = trimmedLine.substring(12).trim();
    }
  }
  
  // Don't forget the last task
  if (currentTask.title && currentTask.description) {
    tasks.push({
      title: currentTask.title,
      description: currentTask.description
    });
  }
  
  // Validate we got 5 tasks
  if (tasks.length !== 5) {
    console.warn(`[BulkTaskGeneration] Expected 5 tasks, got ${tasks.length}. Proceeding with available tasks.`);
  }
  
  if (tasks.length === 0) {
    throw new Error('Failed to parse any tasks from AI response');
  }
  
  console.log(`[BulkTaskGeneration] Successfully parsed ${tasks.length} tasks from AI response`);
  return tasks;
}

/**
 * Hook for generating 5 tasks at once in parallel
 * Simplified approach - no slot management, no auto-replacement
 */
export const useBulkTaskGeneration = (goalId: string) => {
  const { user } = useUser();
  const { executeAIOperation } = useAIOperationWrapper();
  const { generateCompleteTask: generateTaskWithWrapper } = useTaskGeneration();
  const usageData = useAIUsageData();
  const { openOutOfCreditsModal } = useGlobalModals();
  const queryClient = useQueryClient();

  // Create a version of addTaskMutation that doesn't auto-invalidate
  const addTaskMutationWithoutInvalidation = useMutation({
    mutationFn: async (taskData: { title: string; description: string | null }) => {
      if (!user) throw new Error("Not signed in");
      return createSuggestedTask({
        goalId,
        userId: user.id,
        title: taskData.title,
        description: taskData.description,
        status: "pending",
      });
    },
    // No onSuccess invalidation - we'll handle it manually
  });

  return useMutation({
    mutationFn: async (params: BulkTaskGenerationParams) => {
      const { goalTitle, goalDescription, openaiThreadId, userContext } = params;

      if (!user?.id) {
        throw new Error("User must be authenticated");
      }

      if (!openaiThreadId) {
        throw new Error("Goal does not have an OpenAI thread");
      }

      // ✅ UPFRONT CREDIT CHECK - Prevent operation if no credits available
      if (usageData.remainingBalance === 0) {
        console.log('[BulkTaskGeneration] Insufficient credits - blocking task generation and showing modal');
        openOutOfCreditsModal();
        throw new Error('Insufficient credits');
      }

      console.log(`[BulkTaskGeneration] Credits available (${usageData.remainingBalance}) - proceeding with bulk generation`);
      console.log('[BulkTaskGeneration] Generating 5 tasks in single API call...');

      // Generate 5 tasks in a single API call
      const bulkTaskResponse = await generateBulkTasks(
        openaiThreadId,
        goalTitle,
        goalDescription || '',
        goalId,
        user.id,
        executeAIOperation,
        userContext // NEW: Pass user context
      );
      
      console.log(`[BulkTaskGeneration] Generated ${bulkTaskResponse.length} tasks successfully`);
      const generatedTasks = bulkTaskResponse;

      // Add all tasks to suggestions in parallel
      const addPromises = generatedTasks.map(async (task, index) => {
        try {
          const result = await addTaskMutationWithoutInvalidation.mutateAsync({
            title: task.title,
            description: task.description,
          });
          console.log(`[BulkTaskGeneration] Task ${index + 1} saved to suggestions`);
          return result;
        } catch (error) {
          console.error(`[BulkTaskGeneration] Failed to save task ${index + 1}:`, error);
          throw error;
        }
      });

      const savedTasks = await Promise.all(addPromises);
      console.log(`[BulkTaskGeneration] All ${savedTasks.length} tasks saved to suggestions`);

      return savedTasks;
    },
    onSuccess: (savedTasks) => {
      toast.success(`Successfully generated ${savedTasks.length} new task recommendations!`);
      
      // Invalidate task queries to refresh UI
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ["suggestedTasks", user.id, goalId] });
      }
    },
    onError: (error: any) => {
      console.error('[BulkTaskGeneration] Bulk generation failed:', error);
      
      // Handle specific error cases
      if (error.message === 'Insufficient credits') {
        // Credits modal already shown, don't show additional error
        return;
      }
      
      const errorMessage = error?.message || 'Unknown error occurred';
      
      if (errorMessage.toLowerCase().includes('rate limit')) {
        toast.error('Rate limit reached. Please wait before generating more tasks.');
      } else {
        toast.error(`Failed to generate tasks: ${errorMessage}`);
      }
    }
  });
}; 